import { createContext, useContext, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";

const ConsultationContext = createContext({ open: () => {} });

export const useConsultation = () => useContext(ConsultationContext);

const inputCls =
  "w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none transition-colors focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/40";

export function ConsultationProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", {
        name: form.name,
        email: form.email,
        company: form.company,
        message: form.message || "Requesting a free 30-minute consultation call.",
        service: "Free Consultation",
        source: "consultation",
      });
      toast.success("Consultation requested", { description: "We'll be in touch within one business day." });
      setForm({ name: "", email: "", company: "", message: "" });
      setIsOpen(false);
    } catch (err) {
      toast.error("Something went wrong", { description: "Please try again or email hello@techaxisltd.co.uk." });
    } finally {
      setSending(false);
    }
  };

  return (
    <ConsultationContext.Provider value={{ open: () => setIsOpen(true) }}>
      {children}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="border-white/10 bg-navy-900 text-white sm:max-w-md" data-testid="consultation-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Book a free consultation</DialogTitle>
            <DialogDescription className="text-slate-400">
              A free 30-minute discovery call with our team. No obligation, no jargon.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <input required minLength={2} value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} data-testid="consultation-name-input" />
            <input required type="email" value={form.email} onChange={set("email")} placeholder="Work email" className={inputCls} data-testid="consultation-email-input" />
            <input value={form.company} onChange={set("company")} placeholder="Company (optional)" className={inputCls} data-testid="consultation-company-input" />
            <textarea value={form.message} onChange={set("message")} placeholder="What would you like to discuss?" rows={3} className={inputCls} data-testid="consultation-message-input" />
            <button
              type="submit"
              disabled={sending}
              data-testid="consultation-submit-button"
              className="w-full rounded-full bg-electric px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-cyan-400 hover:text-ink disabled:opacity-60"
            >
              {sending ? "Sending..." : "Request my free call"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </ConsultationContext.Provider>
  );
}
